import { UPDATE_CAMPAIGN_SUGGESTION } from "@lib/graphql/mutations";
import { ArrowRight, Bell, Check, Lightbulb, X } from "lucide-react";
import { useMutation } from "urql";

interface SuggestionData {
  id: string;
  suggestionType: string;
  content: string;
  status: string;
  createdAt: string;
}

interface SuggestionsPanelProps {
  suggestions: SuggestionData[];
  onChanged: () => void;
}

const TYPE_CONFIG: Record<string, { label: string; color: string; icon: typeof Lightbulb }> = {
  next_step: {
    label: "Next Step",
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    icon: ArrowRight,
  },
  reminder: {
    label: "Reminder",
    color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    icon: Bell,
  },
  insight: {
    label: "Insight",
    color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
    icon: Lightbulb,
  },
};

export function SuggestionsPanel({ suggestions, onChanged }: SuggestionsPanelProps) {
  const [{ fetching }, updateSuggestion] = useMutation(UPDATE_CAMPAIGN_SUGGESTION);

  const pending = suggestions.filter((s) => s.status === "pending");

  async function handleUpdate(id: string, status: string) {
    const result = await updateSuggestion({ input: { id, status } });
    if (result.data?.updateCampaignSuggestion?.errors?.length === 0) {
      onChanged();
    }
  }

  if (pending.length === 0) {
    return <p className="py-4 text-center text-xs text-text-faint">No suggestions right now</p>;
  }

  return (
    <div className="space-y-2">
      {pending.map((suggestion) => {
        const config = TYPE_CONFIG[suggestion.suggestionType] ?? TYPE_CONFIG.insight;
        const TypeIcon = config.icon;
        return (
          <div
            key={suggestion.id}
            className="flex items-start gap-3 rounded-lg border border-border bg-card p-3"
          >
            <div className="mt-0.5 shrink-0">
              <TypeIcon size={16} className="text-text-muted" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-center gap-2">
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${config.color}`}
                >
                  {config.label}
                </span>
                <span className="text-[10px] text-text-faint">
                  {new Date(suggestion.createdAt).toLocaleDateString()}
                </span>
              </div>
              <p className="text-sm text-text-secondary">{suggestion.content}</p>
            </div>
            <div className="flex shrink-0 gap-1">
              <button
                type="button"
                onClick={() => handleUpdate(suggestion.id, "actioned")}
                disabled={fetching}
                className="rounded p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
                title="Mark as done"
              >
                <Check size={14} />
              </button>
              <button
                type="button"
                onClick={() => handleUpdate(suggestion.id, "dismissed")}
                disabled={fetching}
                className="rounded p-1 text-text-faint hover:bg-card-hover"
                title="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
